import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../components/ProgressBar';
import Footer from '../components/Footer';
import { loadState, updatePage } from '../utils/stateManager';
import './Page6LogAnalytics.css';

function Page6LogAnalytics() {
  const navigate = useNavigate();
  const [useLogAnalytics, setUseLogAnalytics] = useState(false);
  const [environment, setEnvironment] = useState('production');
  const [lawName, setLawName] = useState('');
  const [vmName, setVmName] = useState('');

  useEffect(() => {
    const state = loadState();
    if (state.page6LogAnalytics !== undefined) {
      setUseLogAnalytics(state.page6LogAnalytics.useLogAnalytics || false);
    }
    if (state.page3VMConfig) {
      setEnvironment(state.page3VMConfig.environment || 'production');
      const vm = state.page3VMConfig.finalVMName || state.page3VMConfig.derivedVMName || '';
      setVmName(vm);
      if (vm) {
        setLawName(`law-${vm}`);
      }
    }
  }, []);

  const handleToggle = (checked) => {
    setUseLogAnalytics(checked);
    updatePage('page6LogAnalytics', { useLogAnalytics: checked });
  };

  const handleBack = () => {
    navigate('/page5');
  };

  const handleContinue = () => {
    navigate('/page7');
  };

  const isProduction = environment === 'production';
  const isNonProd = ['development', 'testing', 'staging'].includes(environment);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Azure VM Deployment Wizard</h1>
        <ProgressBar currentPage={6} totalPages={9} />
      </div>

      <div className="page-content">
        <div className="section-header">
          <h2>Step 6: Log Analytics - Monitoring & Logging</h2>
          <span className="badge-optional">OPTIONAL</span>
        </div>

        <div className="what-it-does">
          <h3>📊 What It Does</h3>
          <ul>
            <li>Centralized log collection (system events, performance metrics)</li>
            <li>Query logs across multiple VMs</li>
            <li>Set up alerts (high CPU, low disk space, security events)</li>
            <li>Integrates with Azure Monitor for dashboards and insights</li>
          </ul>
        </div>

        <div className="cost-section">
          <h3>💰 Cost</h3>
          <p className="cost-value">~$2.30/GB</p>
          <p className="cost-note">Typically $10-30/month per VM (varies by log volume)</p>
        </div>

        {isProduction && (
          <div className="recommendation production">
            <h3>✅ Recommended for Production</h3>
            <p>
              <strong>Enable this for production VMs.</strong> Log Analytics provides critical
              visibility into system health, performance, and security. The ability to query
              logs and set up proactive alerts can prevent outages and speed up troubleshooting.
            </p>
          </div>
        )}

        {isNonProd && (
          <div className="recommendation non-prod">
            <h3>💡 Skip for Non-Production (Usually)</h3>
            <p>
              <strong>Most dev/test/staging VMs don't need this.</strong> Azure Metrics (free)
              provide basic monitoring for CPU, memory, and disk. Log Analytics is more useful
              when you need detailed log analysis or centralized monitoring across many VMs.
            </p>
          </div>
        )}

        <div className="when-to-use">
          <h3>✅ When to Use Log Analytics</h3>
          <ul>
            <li>Production VMs requiring proactive monitoring</li>
            <li>VMs with compliance requirements for log retention</li>
            <li>Multi-VM environments needing centralized log queries</li>
            <li>Setting up custom alerts based on log patterns</li>
          </ul>
        </div>

        <div className="when-to-skip">
          <h3>❌ When to Skip</h3>
          <ul>
            <li>Dev/test VMs with short lifespans</li>
            <li>Single VMs without complex monitoring needs</li>
            <li>Budget-conscious non-production environments</li>
            <li>VMs where basic Azure Metrics are sufficient</li>
          </ul>
        </div>

        <div className="decision-section">
          <h3>Your Decision</h3>
          <div className="checkbox-container">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={useLogAnalytics}
                onChange={(e) => handleToggle(e.target.checked)}
              />
              <span className="checkbox-text">
                Create Log Analytics Workspace
              </span>
            </label>
          </div>

          {useLogAnalytics && (
            <div className="selection-info">
              {lawName ? (
                <>
                  <p>
                    ✅ Your Log Analytics workspace: <code><strong>{lawName}</strong></code>
                  </p>
                  <p className="naming-example">
                    Pattern: <code>law-&#123;vmname&#125;</code> (30-day retention)<br />
                    From VM name: "<em>{vmName}</em>"
                  </p>
                </>
              ) : (
                <p>
                  ✅ A Log Analytics workspace will be created with the naming pattern:
                  <code>law-&#123;vmname&#125;</code> (go back to Step 3 to configure your VM name)
                </p>
              )}
            </div>
          )}

          {!useLogAnalytics && (
            <div className="selection-info">
              <p>
                ℹ️ Azure basic metrics (free) will be available for monitoring.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="page-actions">
        <button className="btn-secondary" onClick={handleBack}>
          ← Back
        </button>
        <button className="btn-primary" onClick={handleContinue}>
          Next: Tags →
        </button>
      </div>

      <Footer />
    </div>
  );
}

export default Page6LogAnalytics;
